import React, { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'

import { Box, CircularProgress, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useBoolState, useLazyEffect, useUnmountSignal } from '../../../hooks/commonHooks'
import { MoySkladAttributeModel } from '../../../server/mpsklad_core/Models/MoySkladAttributeModel'
import { useMessages } from '../../../hooks/snackbarHooks'
import { useApi, useLogic, useStore } from '../../../hooks/storeHook'
import { AdditionalPropertiesSettingsField } from '../AdditionalPropertiesSettingsField'
import { required } from '../../../common/objectUtils'
import { MoySkladOrderFeeType } from '../../../server/mpsklad_core/Models/MoySkladOrderFeeType'
import { AdditionalPropertiesSettingsActionButtons } from '../AdditionalPropertiesSettingsActionButtons'
import { RequestCancelledError } from '../../../types/requestCancelledError'

export const MoySkladAdditionalPropertiesSettings =
  observer(() => {
    const {showSuccess, showError} = useMessages()

    const {syncStore: {moySkladAccount}} = useStore()
    const logic = useLogic()
    const api = useApi()

    const [msAttributes, setMsAttributes] = useState<MoySkladAttributeModel[] | null>(null)

    const unmountSignal = useUnmountSignal()

    useLazyEffect(
      async () => {
        try {
          setMsAttributes(await api.userSync.getMoySkladAttributesForCommissions({signal: unmountSignal}))
        } catch (e) {
          if (e instanceof RequestCancelledError) {
            return
          }

          console.error('Failed to get MoySklad attrs', e)
          showError('Не удалось загрузить доп. поля Заказа из МоегоСклада')
        }
      })

    /**
     * Ensures that the given id exists in MS.
     * @param id
     */
    const validateMsAttributeId = useCallback(
      (id: string | undefined) =>
        msAttributes?.some(_ => _.id === id) ? id : undefined,
      [msAttributes])

    const [isLoading, setLoading, setLoaded] = useBoolState()

    const [commissionId, setCommissionId] = useState(
      validateMsAttributeId(moySkladAccount?.commissionFeeAttributeId))

    const [isCreating, setCreating, setCreated] = useBoolState()

    const classes = useStyles()

    useEffect(() =>
        setCommissionId(validateMsAttributeId(moySkladAccount?.commissionFeeAttributeId)),
      [validateMsAttributeId, moySkladAccount?.commissionFeeAttributeId])

    const onCreateAttr =
      (feeType: MoySkladOrderFeeType) =>
        async () => {
          setCreating()

          try {
            const createdAttr = await logic.api.userSync.createMoySkladOrderFeeAttribute(feeType)

            setMsAttributes([...required(msAttributes), createdAttr])

          } catch (e) {
            console.error('Failed to created MS attr', e)
            showError('Не удалось создать поле')
          } finally {
            setCreated()
          }
        }

    const onSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      setLoading()

      try {
        await logic.setMsOrderAttributes({
          commissionFeeAttributeId: commissionId
        })

        showSuccess('Поля сохранены!')
      } catch (ex) {
        console.error('Failed to set MoySklad attrs', ex)
        showError('Произошла ошибка при сохранении полей!')
      } finally {
        setLoaded()
      }
    }

    if (!moySkladAccount || !msAttributes) {
      return <Box
        width="calc(100vw - 240px)" height="100vh" display="flex" alignItems="center"
        justifyContent={'center'}>
        <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
          <CircularProgress/>
        </Box>
      </Box>
    }

    return (
      <Box className={classes.pageContainer}>
        <Typography className={classes.header}>Дополнительные поля заказа Ozon, Wildberries, Yandex.Market</Typography>

        <Grid container spacing={3}>
          <AdditionalPropertiesSettingsField
            value={commissionId}
            setValue={setCommissionId}
            options={required(msAttributes)}
            isCreating={isCreating}
            onCreate={onCreateAttr(MoySkladOrderFeeType.Commission)}
            labelName="Комиссия"/>
        </Grid>

        <AdditionalPropertiesSettingsActionButtons isLoading={isLoading} onSubmit={onSubmit}/>
      </Box>
    )
  })

const useStyles = makeStyles(
  () => ({
    pageContainer: {
      margin: '0 40px 40px 0px',
      width: '100%',
      maxWidth: 'calc(100vw - 340px)'
    },
    header: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '21.6px',
      textAlign: 'left',
      margin: '10px 10px 30px 10px'
    }
  })
)